import { useEffect, useState } from 'react';
import { api, type Decision, type MeetingAction } from '../lib/api.js';
import {
  describeSendError,
  digitsOrBlank,
  friendlySaveError,
  pushFailureText,
  usualDestination,
  usualHint,
  type SendDestination,
} from '../lib/ledgerCopy.js';
import { ActionCard, DecisionCard, type SendChoices } from './NoteCards.js';

export function LedgerPanel({
  meetingId,
  meetingStatus,
  seek,
}: {
  meetingId: string;
  meetingStatus: string;
  seek: (ms: number) => void;
}): JSX.Element {
  const [decisions, setDecisions] = useState<Decision[] | null>(null);
  const [actions, setActions] = useState<MeetingAction[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    void Promise.all([api.listDecisions(meetingId), api.listActions(meetingId)])
      .then(([nextDecisions, nextActions]) => {
        if (!alive) return;
        setDecisions(nextDecisions);
        setActions(nextActions);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      alive = false;
    };
  }, [meetingId, meetingStatus]);

  return (
    <div className="meeting-notes__rail">
      <section className="panel">
        <div className="panel__head">
          <h3 className="panel__title">What we decided</h3>
          <p className="panel__subtle">Edit anytime. Re-summarize keeps your edits.</p>
        </div>
        {error && <div className="form-error">{error}</div>}
        {decisions === null ? (
          <p className="panel__subtle">Loading…</p>
        ) : (
          <DecisionList meetingId={meetingId} rows={decisions} seek={seek} onChange={setDecisions} />
        )}
      </section>

      <section className="panel">
        <div className="panel__head">
          <h3 className="panel__title">Action items</h3>
          <p className="panel__subtle">Check an item when it is done.</p>
        </div>
        {actions === null ? (
          <p className="panel__subtle">Loading…</p>
        ) : (
          <ActionList meetingId={meetingId} rows={actions} seek={seek} onChange={setActions} />
        )}
      </section>
    </div>
  );
}

function DecisionList({
  meetingId,
  rows,
  seek,
  onChange,
}: {
  meetingId: string;
  rows: Decision[];
  seek: (ms: number) => void;
  onChange: (rows: Decision[]) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(row: Decision, patch: Partial<Pick<Decision, 'text' | 'decidedBy' | 'status'>>): Promise<void> {
    const next = await api.updateDecision(row.id, patch);
    onChange(rows.map((item) => (item.id === row.id ? next : item)));
  }

  async function add(): Promise<void> {
    const value = text.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const created = await api.createDecision(meetingId, value);
      onChange([...rows, created]);
      setText('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {rows.length === 0 && <p className="panel__subtle">Nothing decided yet.</p>}
      <ul className="note-list">
        {rows.map((row) => (
          <DecisionCard key={row.id} row={row} seek={seek} onSave={save} />
        ))}
      </ul>
      <form
        className="note-add"
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a decision"
          aria-label="Add a decision"
        />
        <button type="submit" className="btn btn--ghost btn--sm" disabled={busy || !text.trim()}>
          Add
        </button>
      </form>
    </>
  );
}

function ActionList({
  meetingId,
  rows,
  seek,
  onChange,
}: {
  meetingId: string;
  rows: MeetingAction[];
  seek: (ms: number) => void;
  onChange: (rows: MeetingAction[]) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [todolistId, setTodolistId] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [autoPush, setAutoPush] = useState(false);
  const [effectiveProject, setEffectiveProject] = useState(false);
  const [effectiveEmail, setEffectiveEmail] = useState<string | null>(null);
  const [basecampConnected, setBasecampConnected] = useState(false);
  const [basecampConfigured, setBasecampConfigured] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void Promise.all([api.getMeetingPushDefaults(meetingId), api.calendarAccounts()])
      .then(([defaults, accounts]) => {
        if (!alive) return;
        setProjectId(defaults.meeting.basecamp.projectId ?? '');
        setTodolistId(defaults.meeting.basecamp.todolistId ?? '');
        setEmailTo(defaults.meeting.emailTo ?? '');
        setAutoPush(defaults.effective.basecamp.autoPush);
        setEffectiveProject(!!defaults.effective.basecamp.projectId);
        setEffectiveEmail(defaults.effective.emailTo);
        setBasecampConnected(!!accounts.basecamp);
        setBasecampConfigured(accounts.basecampConfigured);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [meetingId]);

  async function reload(): Promise<void> {
    onChange(await api.listActions(meetingId));
  }

  async function save(row: MeetingAction, patch: Partial<Pick<MeetingAction, 'text' | 'owner' | 'status'>>): Promise<void> {
    const next = await api.updateAction(row.id, patch);
    onChange(rows.map((item) => (item.id === row.id ? next : item)));
  }

  async function add(): Promise<void> {
    const value = text.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const created = await api.createAction(meetingId, value);
      onChange([...rows, created]);
      setText('');
    } finally {
      setBusy(false);
    }
  }

  async function saveDestination(): Promise<void> {
    if (!digitsOrBlank(projectId) || !digitsOrBlank(todolistId)) {
      setListError('Use digits for project and list numbers.');
      return;
    }
    setBusy(true);
    setListError(null);
    try {
      const saved = await api.saveMeetingPushDefaults(meetingId, {
        projectId: projectId.trim(),
        todolistId: todolistId.trim(),
        emailTo: emailTo.trim(),
      });
      setProjectId(saved.meeting.basecamp.projectId ?? '');
      setTodolistId(saved.meeting.basecamp.todolistId ?? '');
      setEmailTo(saved.meeting.emailTo ?? '');
      setAutoPush(saved.effective.basecamp.autoPush);
      setEffectiveProject(!!saved.effective.basecamp.projectId);
      setEffectiveEmail(saved.effective.emailTo);
      setNotice('Saved for this meeting.');
    } catch (err) {
      setListError(friendlySaveError(err));
    } finally {
      setBusy(false);
    }
  }

  async function pushAll(destination: SendDestination): Promise<void> {
    setBusy(true);
    setListError(null);
    setNotice(null);
    try {
      const result = await api.pushAllActions(meetingId, { destination });
      const failed = result.results.filter((item) => !item.ok);
      const sent = result.results.length - failed.length;
      if (failed.length === 0) {
        setNotice(destination === 'email' ? 'Emailed the open items.' : 'Sent the open items to Basecamp.');
      } else if (sent === 0) {
        setListError(pushFailureText(failed[0]?.error || failed[0]?.message || 'send_failed'));
      } else {
        setListError(`${sent} sent. ${failed.length} did not go through.`);
      }
    } catch (err) {
      setListError(describeSendError(err));
    } finally {
      try {
        await reload();
      } catch {
        /* the error above is the one to show */
      }
      setBusy(false);
    }
  }

  const basecampReady = basecampConnected && effectiveProject;
  const send: SendChoices = {
    basecampConfigured,
    basecampConnected,
    basecampReady,
    usual: usualDestination(autoPush, basecampReady),
    emailLabel: effectiveEmail ? `Email to ${effectiveEmail}` : 'Email this',
  };
  const openCount = rows.filter((row) => row.status === 'open').length;

  return (
    <>
      {rows.length === 0 && <p className="panel__subtle">No action items yet.</p>}
      <ul className="note-list">
        {rows.map((row) => (
          <ActionCard
            key={row.id}
            row={row}
            seek={seek}
            send={send}
            onSave={save}
            onPush={async (destination) => {
              const result = await api.pushAction(row.id, { destination });
              onChange(rows.map((item) => (item.id === result.action.id ? result.action : item)));
            }}
          />
        ))}
      </ul>
      <form
        className="note-add"
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add an action item"
          aria-label="Add an action item"
        />
        <button type="submit" className="btn btn--ghost btn--sm" disabled={busy || !text.trim()}>
          Add
        </button>
      </form>

      {rows.length > 0 && (
        <details className="note-send">
          <summary>{openCount > 0 ? 'Send open items' : 'Where these go'}</summary>
          <div className="note-send__body">
            {openCount > 0 && <p className="panel__subtle">{usualHint(send.usual, basecampReady)}</p>}
            {openCount > 0 && (
            <div className="note-send__actions">
              {send.usual === 'basecamp' ? (
                <>
                  <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void pushAll('basecamp')}>
                    Send to Basecamp
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void pushAll('email')}>
                    Email them
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void pushAll('email')}>
                    Email them
                  </button>
                  {basecampReady && (
                    <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void pushAll('basecamp')}>
                      Send to Basecamp
                    </button>
                  )}
                </>
              )}
              {!basecampReady && basecampConfigured && (
                <a href="/settings/integrations#basecamp">Set up Basecamp</a>
              )}
            </div>
            )}
            <details>
              <summary>For this meeting only</summary>
              <div className="note-send__body">
                <p className="form-help">Blank fields use Integrations.</p>
                <input
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="Email for this meeting"
                  aria-label="Email for this meeting"
                  type="email"
                />
                <input
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  placeholder="Basecamp project number"
                  aria-label="Basecamp project number for this meeting"
                  inputMode="numeric"
                />
                <input
                  value={todolistId}
                  onChange={(e) => setTodolistId(e.target.value)}
                  placeholder="To-do list number"
                  aria-label="Basecamp to-do list number for this meeting"
                  inputMode="numeric"
                />
                <div>
                  <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void saveDestination()}>
                    Save for this meeting
                  </button>
                </div>
              </div>
            </details>
          </div>
        </details>
      )}
      {notice && <div className="form-success" style={{ marginTop: 8 }}>{notice}</div>}
      {listError && <div className="form-error" style={{ marginTop: 8 }}>{listError}</div>}
    </>
  );
}
